import React from 'react';
import { Box, Paper, Typography, Stack, Chip, Button } from '@mui/material';
import { formatCurrency } from '../../helpers/formatCurrency';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const AutoMatchSuggestions = ({ autoMatches, onSelectMatch }) => {
  if (!autoMatches || autoMatches.length === 0) {
    return null;
  }

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      return format(parseISO(dateString), 'dd/MM/yy', { locale: ptBR });
    } catch {
      return '';
    }
  };

  const getConfidenceColor = (score) => {
    if (score >= 90) return 'success';
    if (score >= 70) return 'warning';
    return 'error';
  };

  // Check if these are bulk groups or individual matches
  const hasBulkGroups = autoMatches.some((match) => match.type === 'bulk_group');

  return (
    <Box
      sx={{
        flexShrink: 0,
        p: 2,
        borderTop: '1px solid rgba(0,0,0,0.06)',
        backgroundColor: 'white',
      }}
    >
      <Paper elevation={1} sx={{ p: 2, borderRadius: 2 }}>
        <Typography variant="subtitle2" fontWeight="600" gutterBottom>
          {hasBulkGroups
            ? 'Grupos para Conciliação em Lote'
            : 'Sugestões de Correspondência Automática'}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {hasBulkGroups
            ? `${autoMatches.length} grupo(s) de transações com valores idênticos encontrado(s).`
            : `${autoMatches.length} correspondência(s) encontrada(s) com base em valor e data.`}
        </Typography>

        <Stack spacing={1.5}>
          {autoMatches.slice(0, 5).map((match, index) => {
            if (match.type === 'bulk_group') {
              // Render bulk group suggestion
              return (
                <Box
                  key={`bulk-${index}`}
                  sx={{
                    p: 1.5,
                    border: 1,
                    borderColor: 'success.main',
                    borderRadius: 1.5,
                    backgroundColor: 'success.50',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <Box flex={1}>
                    <Typography variant="body2" fontWeight="600" noWrap>
                      {match.transactionCount} transações • {formatCurrency(match.amount)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {match.description}
                    </Typography>
                  </Box>

                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip label="Lote" color="success" size="small" sx={{ fontSize: '0.7rem' }} />
                    <Button
                      size="small"
                      variant="contained"
                      color="success"
                      onClick={() => onSelectMatch(match)}
                      sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                    >
                      Criar Lançamentos
                    </Button>
                  </Stack>
                </Box>
              );
            } else {
              // Render individual match suggestion (existing logic)
              const amountMatch =
                Math.abs(Math.abs(match.transaction.amount) - Math.abs(match.journalEntry.valueFinal ?? match.journalEntry.value)) <
                0.01;
              const amountDiff = Math.abs(
                Math.abs(match.transaction.amount) - Math.abs(match.journalEntry.valueFinal ?? match.journalEntry.value)
              );

              let score = 20;
              if (amountMatch) score = 100;
              else if (amountDiff < 10) score = 80;
              else if (amountDiff < 50) score = 60;
              else if (amountDiff < 100) score = 40;

              return (
                <Box
                  key={index}
                  sx={{
                    p: 1.5,
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1.5,
                    backgroundColor: 'grey.50',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <Box flex={1}>
                    <Typography variant="body2" fontWeight="600" noWrap>
                      {match.transaction.description} ↔ {match.journalEntry.description}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatCurrency(Math.abs(match.transaction.amount))} •{' '}
                      {formatDate(match.transaction.datePosted)}
                    </Typography>
                  </Box>

                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip
                      label={`${score}%`}
                      color={getConfidenceColor(score)}
                      size="small"
                      sx={{ fontSize: '0.7rem' }}
                    />
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => onSelectMatch(match)}
                      sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                    >
                      Selecionar
                    </Button>
                  </Stack>
                </Box>
              );
            }
          })}
        </Stack>
      </Paper>
    </Box>
  );
};

export default AutoMatchSuggestions;
